import React, { useCallback, useState } from 'react';
import AccessibleWorkflowDiagram from '@jira/project-settings__accessible-workflow-diagram/src/WrappedDiagram.tsx';
import type {
	Status,
	MetaWorkflowDocument,
} from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import Button from '@atlaskit/button';
import { Box, xcss } from '@atlaskit/primitives';
import AkSpinner from '@atlaskit/spinner';
import EmptyState from '@atlaskit/empty-state';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { JiraModal as ModalDialog } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { PAGE_LAYOUT_OFFSET_TOP } from '@atlassian/jira-common-constants/src/page-layout.tsx';
import errorImage from '@atlassian/jira-illustrations/src/ui/adg4/brand/spots/error/components/error/index.tsx';
import { getAkEmptyStateRenderImageFn } from '@atlassian/jira-illustrations/src/ui/helpers/ak-empty-state/index.tsx';
import { FireScreenAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ZoomControls } from './ZoomControls.tsx';
import type { WorkflowIdentifier } from './types.tsx';
import { useWorkflowData } from './useWorkflowData.tsx';

const closeButton = defineMessage({
	id: 'workflow-view-diagram-modal.close-button',
	defaultMessage: 'Close',
	description: 'Label for the button to close the workflow view diagram modal',
});

const errorMessage = defineMessage({
	defaultMessage: 'We couldn’t load the workflow.',
	id: 'workflow-view-diagram-modal.error-message',
	description: 'Error message for when the modal fails to load its content',
});

export type Props = {
	projectId?: string;
	workflowIdentifier: WorkflowIdentifier;
	onClose: () => void;
};

const INITIAL_ZOOM = 1;
const ERROR_IMAGE_MAX_HEIGHT = 212;

// Workflow Diagram Content Component
const WorkflowDiagramContent = ({
	workflow,
	zoom,
	onZoomChange,
	onZoomUpdate,
}: {
	workflow: MetaWorkflowDocument;
	zoom: number;
	onZoomChange: (zoom: number) => void;
	onZoomUpdate: (zoom: number) => void;
}) => {
	const statuses = workflow.visibleStatuses.map<Status>(
		({ ref: statusId }) => workflow.allStatuses[statusId],
	);

	return (
		<Box xcss={wrapperStyles}>
			<Box xcss={diagramContentWrapperStyles}>
				<AccessibleWorkflowDiagram
					startNodeLayout={workflow.startNodeLayout}
					statuses={statuses}
					transitions={Object.values(workflow.transitions)}
					zoom={zoom}
					isImmutable
					onZoomUpdate={onZoomUpdate}
					collapseTransitionLabels
					shouldShowGlobalLoops
					{...(expVal('sprt_hix_9246_wfe_diagram_complete_workflowscope', 'isEnabled', false) && {
						workflowScope: workflow.scope.type,
					})}
				/>
			</Box>
			<ZoomControls zoom={zoom} onZoomChange={onZoomChange} />
		</Box>
	);
};

// Main Component
export const WorkflowViewDiagramModal = ({
	projectId,
	workflowIdentifier,
	onClose,
}: Props): React.JSX.Element => {
	const cloudId = useCloudId();
	const { formatMessage } = useIntl();
	const [zoom, setZoom] = useState(INITIAL_ZOOM);

	const {
		data: workflow,
		isLoading,
		hasError,
		customErrorMessage,
	} = useWorkflowData(cloudId, workflowIdentifier, projectId);

	const onZoomChange = useCallback((newZoomValue: number) => {
		setZoom(newZoomValue);
	}, []);

	return (
		<>
			<ModalDialog
				messageId="workflow-view-diagram-modal.modal-dialog.workflow-diagram-view-modal"
				messageType="transactional"
				shouldCloseOnEscapePress
				shouldCloseOnOverlayClick
				onClose={onClose}
				width="100%"
			>
				<Box xcss={modalContentWrapperStyles}>
					<ModalHeader hasCloseButton>
						{workflow && !isLoading && (
							<ModalTitle testId="workflow-view-diagram-modal.modal-title">
								{workflow.name}
							</ModalTitle>
						)}
					</ModalHeader>
					<ModalBody>
						<Box xcss={contentStyles}>
							{(() => {
								if (!hasError && isLoading && !workflow) {
									return (
										// Loading state
										<Box xcss={centerWrapperStyles} testId="workflow-view-diagram-modal.loading">
											<AkSpinner size="large" />
										</Box>
									);
								}
								if ((hasError && !isLoading) || !workflow) {
									return (
										// Error state
										<EmptyState
											header={formatMessage(errorMessage)}
											maxImageHeight={ERROR_IMAGE_MAX_HEIGHT}
											renderImage={getAkEmptyStateRenderImageFn(errorImage)}
											description={customErrorMessage || undefined}
										/>
									);
								}
								return (
									<WorkflowDiagramContent
										workflow={workflow}
										zoom={zoom}
										onZoomChange={onZoomChange}
										onZoomUpdate={setZoom}
									/>
								);
							})()}
						</Box>
					</ModalBody>
					<ModalFooter>
						<Button onClick={onClose} appearance="subtle">
							{formatMessage(closeButton)}
						</Button>
					</ModalFooter>
				</Box>
			</ModalDialog>
			<FireScreenAnalytics />
		</>
	);
};

const centerWrapperStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexGrow: 1,
	height: '100%',
});

const wrapperStyles = xcss({
	display: 'flex',
	flexGrow: '1',
	minHeight: '352px',
	boxShadow: 'elevation.shadow.raised',
});

const diagramContentWrapperStyles = xcss({
	width: '100%',
	height: 'auto',
});

const contentStyles = xcss({
	display: 'flex',
	justifyContent: 'space-between',
	flexDirection: 'column',
	height: '100%',
});

const getmodalcontentwrapperheight = () =>
	`calc(100vh - ${PAGE_LAYOUT_OFFSET_TOP} - ${PAGE_LAYOUT_OFFSET_TOP})`;

const modalContentWrapperStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	height: getmodalcontentwrapperheight(),
	display: 'flex',
	flexGrow: 1,
	flexDirection: 'column',
	justifyContent: 'space-between',
	borderRadius: 'radius.small',
});
